import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const NEWS_CATEGORIES = [
  "legal",
  "labour",
  "immigration",
  "compliance",
  "consumer",
  "education",
  "general",
] as const;

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

export const listPublicNews = createServerFn({ method: "GET" }).handler(
  async () => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("news_posts")
      .select(
        "id, title, slug, excerpt, category, source_name, source_url, cover_image_url, published_at",
      )
      .eq("is_published", true)
      .order("published_at", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    return { posts: data ?? [] };
  },
);

export const getPublicNewsPost = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) =>
    z.object({ slug: z.string().min(1).max(200) }).parse(i),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: row, error } = await supabaseAdmin
      .from("news_posts")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { post: row };
  });

export const adminListNews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data, error } = await supabaseAdmin
      .from("news_posts")
      .select("*")
      .order("published_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { posts: data ?? [] };
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(3).max(200),
  excerpt: z.string().trim().max(500).optional().default(""),
  body: z.string().trim().max(20000).optional().default(""),
  category: z.enum(NEWS_CATEGORIES).default("general"),
  source_name: z.string().trim().max(120).optional().default(""),
  source_url: z.string().trim().url().or(z.literal("")).optional().default(""),
  cover_image_url: z
    .string()
    .trim()
    .url()
    .or(z.literal(""))
    .optional()
    .default(""),
  is_published: z.boolean().default(true),
});

export const adminUpsertNews = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => upsertSchema.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("news_posts")
        .update({
          title: data.title,
          excerpt: data.excerpt,
          body: data.body,
          category: data.category,
          source_name: data.source_name,
          source_url: data.source_url,
          cover_image_url: data.cover_image_url,
          is_published: data.is_published,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const baseSlug = slugify(data.title) || "post";
    const slug = `${baseSlug}-${Math.random().toString(36).slice(2, 6)}`;
    const { error, data: row } = await supabaseAdmin
      .from("news_posts")
      .insert({
        title: data.title,
        slug,
        excerpt: data.excerpt,
        body: data.body,
        category: data.category,
        source_name: data.source_name,
        source_url: data.source_url,
        cover_image_url: data.cover_image_url,
        is_published: data.is_published,
        created_by: context.userId,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const adminDeleteNews = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ id: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin
      .from("news_posts")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
